import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'lv';

interface Settings {
  language: Language;
  notificationsEnabled: boolean;
  mechanicSharingEnabled: boolean;
  distanceUnit?: 'km' | 'mi';
  fuelUnit?: 'L' | 'gal';
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (settings: Partial<Settings>) => void;
  t: (key: string) => string;
}

const defaultSettings: Settings = {
  language: 'en',
  notificationsEnabled: true,
  mechanicSharingEnabled: true,
  distanceUnit: 'km',
  fuelUnit: 'L',
};

// Basic translations
const translations = {
  en: {
    'home.title': 'Home',
    'home.carHealth': 'Car Health',
    'home.logIssue': 'Log Issue',
    'home.recentIssues': 'Recent Issues',
    'home.viewReports': 'View Reports',
    'home.noCars': 'No cars added yet',
    'home.addCar': 'Add first car',
    
    'cars.title': 'My Cars',
    'cars.addCar': 'Add car',
    'cars.noCars': 'No cars added yet',
    'cars.edit': 'Edit Car',
    
    'car.make': 'Make',
    'car.model': 'Model',
    'car.year': 'Year',
    'car.nickname': 'Nickname',
    'car.vin': 'VIN',
    'car.licensePlate': 'License Plate',
    'car.mileage': 'Mileage',
    'car.transmission': 'Transmission',
    'car.manual': 'Manual',
    'car.automatic': 'Automatic',
    // Fuel type labels
    'car.fuelType': 'Fuel type',
    'car.fuelType.gasoline': 'Gasoline',
    'car.fuelType.diesel': 'Diesel',
    'car.fuelType.electric': 'Electric',
    'car.fuelType.hybrid': 'Hybrid',
    'car.fuelType.lpg': 'LPG',
    'car.fuelType.cng': 'CNG',
    'car.save': 'Save car',
    'car.edit': 'Edit',
    'car.delete': 'Delete',
    'car.maintenance': 'Maintenance History',
    'car.issues': 'Issues',
    'car.healthScore': 'Health Score',
    
    'logSymptom.title': 'Log Symptom',
    'logSymptom.description': 'Describe what\'s happening with your car',
    'logSymptom.placeholder': 'E.g., My car makes a rattling noise when I go over bumps...',
    'logSymptom.addImages': 'Add images',
    'logSymptom.addVideo': 'Add video',
    'logSymptom.submit': 'Submit',
    
    'reports.title': 'Reports',
    'reports.noReports': 'No reports yet',

    'credits.title': 'Credits',
    'credits.icons': 'Icons',
    
    'settings.title': 'Settings',
    'settings.language': 'Language',
    'settings.english': 'English',
    'settings.latvian': 'Latvian',
    'settings.notifications': 'Notifications',
    'settings.mechanicSharing': 'Mechanic Link Sharing',
    'settings.mechanicSharingDesc': 'Allow sharing reports with mechanics',
    'settings.logout': 'Log Out',
    'settings.logoutButton': 'Log Out of Your Account',
    
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.back': 'Back',
  },
  lv: {
    'home.title': 'Sākums',
    'home.carHealth': 'Auto veselība',
    'home.logIssue': 'Reģistrēt problēmu',
    'home.recentIssues': 'Pēdējās problēmas',
    'home.viewReports': 'Skatīt atskaites',
    'home.noCars': 'Vēl nav pievienotu auto',
    'home.addCar': 'Pievienot pirmo auto',
    
    'cars.title': 'Mani auto',
    'cars.addCar': 'Pievienot auto',
    'cars.noCars': 'Vēl nav pievienotu auto',
    'cars.edit': 'Rediģēt auto',
    
    'car.make': 'Marka',
    'car.model': 'Modelis',
    'car.year': 'Gads',
    'car.nickname': 'Segvārds',
    'car.vin': 'VIN',
    'car.licensePlate': 'Reģistrācijas numurs',
    'car.mileage': 'Nobraukums',
    'car.transmission': 'Transmisija',
    'car.manual': 'Manuālā',
    'car.automatic': 'Automātiskā',
    // Fuel type labels
    'car.fuelType': 'Degvielas tips',
    'car.fuelType.gasoline': 'Benzīns',
    'car.fuelType.diesel': 'Dīzelis',
    'car.fuelType.electric': 'Elektriskā',
    'car.fuelType.hybrid': 'Hibrīds',
    'car.fuelType.lpg': 'LPG',
    'car.fuelType.cng': 'CNG',
    'car.save': 'Saglabāt auto',
    'car.edit': 'Rediģēt',
    'car.delete': 'Dzēst',
    'car.maintenance': 'Apkopes vēsture',
    'car.issues': 'Problēmas',
    'car.healthScore': 'Veselības rādītājs',
    
    'logSymptom.title': 'Reģistrēt simptomu',
    'logSymptom.description': 'Aprakstiet, kas notiek ar jūsu auto',
    'logSymptom.placeholder': 'Piem., Mans auto rada grabošu troksni, kad braucu pāri nelīdzenumiem...',
    'logSymptom.addImages': 'Pievienot attēlus',
    'logSymptom.addVideo': 'Pievienot video',
    'logSymptom.submit': 'Iesniegt',
    
    'reports.title': 'Atskaites',
    'reports.noReports': 'Vēl nav atskaišu',

    'credits.title': 'Autortiesības',
    'credits.icons': 'Ikonas',
    
    'settings.title': 'Iestatījumi',
    'settings.language': 'Valoda',
    'settings.english': 'Angļu',
    'settings.latvian': 'Latviešu',
    'settings.notifications': 'Paziņojumi',
    'settings.mechanicSharing': 'Mehāniķu saites kopīgošana',
    'settings.mechanicSharingDesc': 'Atļaut kopīgot atskaites ar mehāniķiem',
    'settings.logout': 'Izrakstīties',
    'settings.logoutButton': 'Izrakstīties no sava konta',
    
    'common.save': 'Saglabāt',
    'common.cancel': 'Atcelt',
    'common.delete': 'Dzēst',
    'common.edit': 'Rediģēt',
    'common.back': 'Atpakaļ',
  }
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(() => {
    const savedSettings = localStorage.getItem('settings');
    return savedSettings ? JSON.parse(savedSettings) : defaultSettings;
  });

  useEffect(() => {
    localStorage.setItem('settings', JSON.stringify(settings));
  }, [settings]);

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings(prevSettings => ({ ...prevSettings, ...newSettings }));
  };

  const t = (key: string): string => {
    const currentTranslations = translations[settings.language];
    return currentTranslations[key] || key;
  };

  const value = {
    settings,
    updateSettings,
    t,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};
